import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TopBar from '../components/TopBar';
import { useUser } from '../context/UserContext';
import userService from '../services/userService';
import { deleteScipCip, fetchScipQuery } from '../components/api';

const specialScopeIso3 = new Set(['HRV']);
const specialScopeCountryNames = new Set(['hrv']);

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-GB');
};

const formatStatus = (stageValue) => {
  if (stageValue === null || typeof stageValue === 'undefined') return '';
  const stage = Number(stageValue);
  const map = {
    0: 'Draft',
    1: 'In Progress',
    2: 'Submitted',
    3: 'Finalized',
    4: 'Locked'
  };
  return Number.isFinite(stage) && map[stage] ? map[stage] : String(stageValue);
};

const SCIPTool = ({
  userClaims,
  isEmbedded = false,
  detailPathPrefix = '/scip-tool',
  backHref = '/scip'
}) => {
  const { user } = useUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'UpdatedDate', direction: 'desc' });
  const [deletingId, setDeletingId] = useState(null);

  const readRowIso3 = (row) => {
    const value = row?.ISO3 ?? row?.IsoCode ?? row?.iso3 ?? row?.country_iso3 ?? '';
    return String(value || '').trim().toUpperCase();
  };

  const readRowCountryName = (row) => {
    const value = row?.CountryName ?? row?.Country ?? row?.country ?? row?.country_name ?? row?.Name ?? '';
    return String(value || '').trim().toLowerCase();
  };

  const resolveRegionIso3 = useCallback(async () => {
    const countries = await userService.getAvailableCountries();
    const userIso3 = String(user?.country_iso3 || '').toUpperCase();
    const userCountryName = String(user?.country || '').toLowerCase();
    const current = countries.find((c) => {
      const iso3 = String(c?.iso3 || '').toUpperCase();
      const countryName = String(c?.Country_Name || '').toLowerCase();
      return (userIso3 && iso3 === userIso3) || (userCountryName && countryName === userCountryName);
    });
    const region = current?.UNICEF_RO || current?.region || current?.Region || '';
    if (!region) return [];
    const regionCountries = await userService.getCountriesByRegion(region);
    return regionCountries.map((c) => String(c?.iso3 || '').trim().toUpperCase()).filter(Boolean);
  }, [user?.country, user?.country_iso3]);

  useEffect(() => {
    let mounted = true;
    const loadScipData = async () => {
      if (!user) return;
      setLoading(true);
      setError('');
      try {
        const accessLevel = String(user?.accessLevel || '').toLowerCase();
        let visibleIso3 = null;
        let visibleCountryNames = null;

        if (accessLevel === 'global' || accessLevel === 'admin') {
          visibleIso3 = null;
          visibleCountryNames = null;
        } else if (accessLevel === 'regional') {
          const fromUserIso = Array.isArray(user?.allowedCountriesIso3)
            ? user.allowedCountriesIso3.map((c) => String(c || '').trim().toUpperCase()).filter(Boolean)
            : [];
          const fromUserNames = Array.isArray(user?.allowedCountries)
            ? user.allowedCountries.map((c) => String(c || '').trim()).filter(Boolean)
            : [];
          visibleIso3 = fromUserIso.length > 0 ? fromUserIso : await resolveRegionIso3();
          visibleCountryNames = fromUserNames;
        } else {
          const primaryIso3 = String(
            user?.country_iso3 || (Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3[0] : '')
          ).trim().toUpperCase();
          const primaryCountryName = String(
            user?.country || (Array.isArray(user?.allowedCountries) ? user.allowedCountries[0] : '')
          ).trim();
          visibleIso3 = primaryIso3 ? [primaryIso3] : [];
          visibleCountryNames = primaryCountryName ? [primaryCountryName] : [];
        }

        if (Array.isArray(visibleIso3)) {
          visibleIso3 = visibleIso3.filter((iso3) => !specialScopeIso3.has(String(iso3 || '').toUpperCase()));
        }
        if (Array.isArray(visibleCountryNames)) {
          visibleCountryNames = visibleCountryNames.filter(
            (name) => !specialScopeCountryNames.has(String(name || '').trim().toLowerCase())
          );
        }

        const hasIsoScope = Array.isArray(visibleIso3) && visibleIso3.length > 0;
        const hasNameScope = Array.isArray(visibleCountryNames) && visibleCountryNames.length > 0;
        if ((Array.isArray(visibleIso3) || Array.isArray(visibleCountryNames)) && !hasIsoScope && !hasNameScope) {
          visibleIso3 = null;
          visibleCountryNames = null;
        }

        const params = { page_size: 200, offset: 0 };
        let countryFilterClause = '';
        if (Array.isArray(visibleIso3) || Array.isArray(visibleCountryNames)) {
          const filterParts = [];
          if (hasIsoScope) {
            const isoPlaceholders = visibleIso3.map((_, idx) => `@iso3_${idx}`).join(', ');
            filterParts.push(`CM.IsoCode IN (${isoPlaceholders})`);
            visibleIso3.forEach((iso3, idx) => {
              params[`iso3_${idx}`] = iso3;
            });
          }
          if (hasNameScope) {
            const countryPlaceholders = visibleCountryNames.map((_, idx) => `@country_${idx}`).join(', ');
            filterParts.push(`CM.Name IN (${countryPlaceholders})`);
            visibleCountryNames.forEach((countryName, idx) => {
              params[`country_${idx}`] = countryName;
            });
          }
          countryFilterClause = ` AND (${filterParts.join(' OR ')})`;
        }

        const query = `
          SELECT
            CBI.Id,
            CBI.Name,
            CM.Name AS CountryName,
            CM.IsoCode AS ISO3,
            CM.UnicefRegionName,
            CM.WhoRegionCode,
            AT.Val AS AssessmentType,
            PF.Val AS ProgrammeFocus,
            ASCP.Val AS AssessmentScope,
            CBI.StartDate,
            CBI.DurationInYear,
            CBI.FinalizedDate,
            CBI.Stage,
            CBI.LockDate,
            CBI.IsPermenentlyLock,
            CBI.RejectedCount,
            CBI.UpdatedDate,
            CBI.UpdatedBy
          FROM SCIP.CipBasicInformation CBI
          LEFT JOIN SCIP.CountryMaster CM ON CM.Id = CBI.CountryId
          LEFT JOIN SCIP.GenericDataMaster GMAT ON GMAT.Id = CBI.AssessmentTypeId
          LEFT JOIN SCIP.GenericDataMaster GMPF ON GMPF.Id = CBI.ProgrammeFocusId
          LEFT JOIN SCIP.GenericDataMaster GMAS ON GMAS.Id = CBI.AssessmentScopeId
          OUTER APPLY (
            SELECT TOP 1 v.[Value] AS Val
            FROM OPENJSON(GMAT.NameJson)
            WITH ([Key] NVARCHAR(10) '$.Key', [Value] NVARCHAR(400) '$.Value') v
            WHERE v.[Key] = 'EN'
          ) AT
          OUTER APPLY (
            SELECT TOP 1 v.[Value] AS Val
            FROM OPENJSON(GMPF.NameJson)
            WITH ([Key] NVARCHAR(10) '$.Key', [Value] NVARCHAR(400) '$.Value') v
            WHERE v.[Key] = 'EN'
          ) PF
          OUTER APPLY (
            SELECT TOP 1 v.[Value] AS Val
            FROM OPENJSON(GMAS.NameJson)
            WITH ([Key] NVARCHAR(10) '$.Key', [Value] NVARCHAR(400) '$.Value') v
            WHERE v.[Key] = 'EN'
          ) ASCP
          WHERE 1 = 1
          ${countryFilterClause}
          ORDER BY CBI.UpdatedDate DESC
          OFFSET @offset ROWS FETCH NEXT @page_size ROWS ONLY
        `;

        let fetchedRows = [];
        try {
          fetchedRows = await fetchScipQuery(query, params);
        } catch (primaryError) {
          const message = String(primaryError?.message || '');
          if (!message.includes("Invalid column name 'Name'")) throw primaryError;

          const fallbackQuery = `
            SELECT CBI.*, CM.*
            FROM SCIP.CipBasicInformation CBI
            LEFT JOIN SCIP.CountryMaster CM ON CM.Id = CBI.CountryId
            ORDER BY CBI.UpdatedDate DESC
            OFFSET @offset ROWS FETCH NEXT @page_size ROWS ONLY
          `;
          const fallbackRows = await fetchScipQuery(fallbackQuery, params);
          const scopedRows = (Array.isArray(visibleIso3) || Array.isArray(visibleCountryNames))
            ? (Array.isArray(fallbackRows) ? fallbackRows : []).filter((r) =>
                (hasIsoScope && visibleIso3.some((iso3) => readRowIso3(r) === String(iso3 || '').trim().toUpperCase())) ||
                (hasNameScope && visibleCountryNames.some((countryName) => readRowCountryName(r) === String(countryName || '').trim().toLowerCase()))
              )
            : (Array.isArray(fallbackRows) ? fallbackRows : []);
          fetchedRows = scopedRows;
        }

        if (mounted) setRows(Array.isArray(fetchedRows) ? fetchedRows : []);
      } catch (e) {
        if (mounted) {
          setError(e?.message || 'Failed to load SCIP data');
          setRows([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadScipData();
    return () => {
      mounted = false;
    };
  }, [user, resolveRegionIso3]);

  const columnDefs = useMemo(() => ([
    { id: 'UnicefRegionName', label: 'UNICEF Region', sortable: true, getValue: (r) => r?.UnicefRegionName || '' },
    { id: 'WhoRegionCode', label: 'WHO Region', sortable: true, getValue: (r) => r?.WhoRegionCode || '' },
    { id: 'CountryName', label: 'Country', sortable: true, getValue: (r) => r?.CountryName || '' },
    { id: 'SubNationalRegion', label: 'Sub-National Region', sortable: true, getValue: (r) => r?.SubNationalRegion || r?.SubNationalRegionName || '' },
    { id: 'Name', label: 'Name', sortable: true, getValue: (r) => r?.Name || '' },
    { id: 'StartDate', label: 'cIP Start Date', sortable: true, getValue: (r) => r?.StartDate || '', render: (v) => formatDate(v) },
    { id: 'UpdatedDate', label: 'Last Updated', sortable: true, getValue: (r) => r?.UpdatedDate || '', render: (v) => formatDate(v) },
    { id: 'Stage', label: 'Status', sortable: true, getValue: (r) => r?.Stage, render: (v) => formatStatus(v) },
    { id: 'Actions', label: 'Actions', sortable: false, getValue: () => '' }
  ]), []);

  const sortedRows = useMemo(() => {
    const rowsCopy = Array.isArray(rows) ? [...rows] : [];
    const sortColumn = columnDefs.find((c) => c.id === sortConfig.key);
    if (!sortColumn || !sortColumn.sortable) return rowsCopy;
    rowsCopy.sort((a, b) => {
      const av = sortColumn.getValue(a);
      const bv = sortColumn.getValue(b);
      const dateA = new Date(av);
      const dateB = new Date(bv);
      if (!Number.isNaN(dateA.getTime()) && !Number.isNaN(dateB.getTime())) {
        return sortConfig.direction === 'asc' ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
      }
      const numA = Number(av);
      const numB = Number(bv);
      if (Number.isFinite(numA) && Number.isFinite(numB)) {
        return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
      }
      const strA = String(av ?? '').toLowerCase();
      const strB = String(bv ?? '').toLowerCase();
      if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return rowsCopy;
  }, [rows, columnDefs, sortConfig]);

  const onSort = (columnId, sortable) => {
    if (!sortable) return;
    setSortConfig((prev) => prev.key === columnId
      ? { key: columnId, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key: columnId, direction: 'asc' });
  };

  const onDelete = async (row) => {
    const cipId = row?.Id;
    if (!cipId) return;
    const confirmed = window.confirm(`Delete CIP "${row?.Name || cipId}"? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      setDeletingId(cipId);
      await deleteScipCip(cipId);
      setRows((prev) => (Array.isArray(prev) ? prev.filter((r) => Number(r?.Id) !== Number(cipId)) : []));
    } catch (e) {
      window.alert(e?.message || 'Failed to delete CIP');
    } finally {
      setDeletingId(null);
    }
  };

  const content = (
    <div className={isEmbedded ? '' : 'min-h-screen bg-gray-100'}>
      {!isEmbedded && <TopBar currentPage="scip-tool" userClaims={userClaims} />}
      <div className={isEmbedded ? 'space-y-4' : 'p-4'}>
        <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-800">SCIP Tool</h2>
              <div className="text-sm text-gray-500">CIPs visible to your access scope</div>
            </div>
            <a href={backHref} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md">Back to Dashboard</a>
          </div>

          {loading && <div className="text-gray-600">Loading SCIP CIPs...</div>}
          {error && <div className="text-red-600">{error}</div>}
          {!loading && !error && <div className="text-sm text-gray-600">Total CIPs: {rows.length}</div>}

          {!loading && !error && rows.length > 0 && (
            <div className="overflow-auto max-h-[65vh] border rounded-lg">
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr>
                    {columnDefs.map((col) => (
                      <th key={col.id} className="px-3 py-2 border-b bg-gray-50 text-left font-semibold text-gray-700 align-top">
                        <button type="button" className={col.sortable ? 'hover:text-blue-700' : 'cursor-default'} onClick={() => onSort(col.id, col.sortable)}>
                          {col.label}
                          {col.sortable && sortConfig.key === col.id && <span className="ml-1">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row, idx) => (
                    <tr key={`scip-row-${idx}`} className="odd:bg-white even:bg-gray-50">
                      {columnDefs.map((col) => (
                        <td key={`${idx}-${col.id}`} className="px-3 py-2 border-b text-gray-700 align-top">
                          {col.id === 'Actions' ? (
                            <div className="flex items-center gap-3">
                              <a
                                className="text-blue-600 hover:text-blue-800 underline"
                                href={`${detailPathPrefix}/${encodeURIComponent(String(row?.Id || ''))}`}
                              >
                                Open
                              </a>
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-800 underline disabled:opacity-60"
                                onClick={() => onDelete(row)}
                                disabled={deletingId === row?.Id}
                              >
                                {deletingId === row?.Id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          ) : (
                            (() => {
                              const rawValue = col.getValue(row);
                              const display = typeof col.render === 'function' ? col.render(rawValue, row) : rawValue;
                              return (
                                <div className="whitespace-normal break-words">
                                  {display == null ? '' : String(display)}
                                </div>
                              );
                            })()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && rows.length === 0 && <div className="text-gray-500">No CIPs found for your access scope.</div>}
        </div>
      </div>
    </div>
  );

  return content;
};

export default SCIPTool;

